import { Button, Checkbox, View } from "@/components/ui";
import {
  ServiceProfileServiceIdMap,
  ServiceProfileTypeMap,
  buildServiceProfileAttributes,
  transformServiceProfile,
  type ServiceProfileType,
} from "../lib";
import type { PetProfileFormReturn } from "./pet/usePetProfileForm";
import {
  type PetProfileFormData,
  usePetProfileForm,
} from "./pet/usePetProfileForm";
import { PetProfileForm } from "./pet/form";
import { validatePetProfile } from "./pet/lib";
import { ClientInstance } from "@/lib/graphql/apollo";
import {
  ClientServiceProfileByFiltersDocument,
  ClientServiceProfileFragmentFragmentDoc,
  ClientServiceProfileStatusesEnum,
} from "@/__generated__/graphql";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Redirect, useRoute } from "@/components/shared/router";
import { Divider } from "@/components/shared/divider";
import { Typography } from "@/components/shared/typography";
import { useCreateClientServiceProfileMutation } from "@/gqlhooks/profile/useCreateClientServiceProfileMutation";
import { useUpdateClientServiceProfileMutation } from "@/gqlhooks/profile/useUpdateClientServiceProfileMutation";
import { useDeleteClientServiceProfileMutation } from "@/gqlhooks/profile/useDeleteClientServiceProfileMutation";
import { showToast } from "@/components/ui/toast/show-toast";
import { getErrorMessage } from "@/lib/helpers/string";
import { collectError } from "@/lib/monitoring/sentry";
import { useParams } from "@/components/hooks/use-params";
import { ServiceProfileFormScreen } from "./screen";
import type { ElderCareFormReturn } from "./elder/useElderCareForm";
import type { UseFormReturn } from "react-hook-form";
import { FormProvider as RHFFormProvider, type Control } from "react-hook-form";
import { ElderCareForm } from "./elder/form";
import { useWindowDimensions } from "@/components/hooks/use-window-dimension";
import {
  useElderCareForm,
  type ElderCareFormData,
} from "./elder/useElderCareForm";
import type { BabySitterFormData } from "./baby/useBabyProfileForm";
import {
  useBabySitterForm,
  type BabySitterFormReturn,
} from "./baby/useBabyProfileForm";
import { BabySitterForm } from "./baby/form";
import { getPlatform } from "@/lib/utils";
import { useApolloClient } from "@apollo/client";
import { useAuthState } from "@/store/auth";

const createFormContainer = <
  T extends PetProfileFormReturn | ElderCareFormReturn | BabySitterFormReturn,
  Data extends T["defaultValues"],
>(
  type: ServiceProfileType,
  useHookForm: () => T,
  Form: React.ComponentType<{
    control: Control<Data>;
  }>,
) =>
  forwardRef<
    { submit: () => void },
    {
      id?: string;
      onSave: (data: Data) => void;
      children: React.ReactNode;
    }
  >(({ id, onSave, children }, ref) => {
    const isEdit = !!id;

    const methods = useHookForm();

    useImperativeHandle(ref, () => ({
      submit: methods.handleSubmit((data) => onSave(data as Data)),
    }));

    useEffect(() => {
      if (isEdit) {
        const profile = ClientInstance.readFragment({
          id: `ClientServiceProfile:${id}`,
          fragment: ClientServiceProfileFragmentFragmentDoc,
        });

        if (profile) {
          const profileAttributes = buildServiceProfileAttributes(
            type,
            profile.profileAttributes as Record<string, unknown>,
          );

          methods.reset({
            ...profileAttributes,
            name: profile.name,
          });
        }
      } else {
        methods.reset({ ...methods.defaultValues });
      }
    }, [isEdit]);

    return (
      <View className="px-4 py-4 md:px-0">
        <RHFFormProvider {...(methods as unknown as UseFormReturn<Data>)}>
          <Form control={methods.control as unknown as Control<Data>} />
        </RHFFormProvider>
        {children}
      </View>
    );
  });

const FORM_MAP = {
  pet: createFormContainer("pet", usePetProfileForm, PetProfileForm),
  elder: createFormContainer("elder", useElderCareForm, ElderCareForm),
  baby: createFormContainer("baby", useBabySitterForm, BabySitterForm),
};

export const ServiceProfileFormContainer = () => {
  const { createProfile, loading: createProfileLoading } =
    useCreateClientServiceProfileMutation();
  const [updateProfile, { loading: updateProfileLoading }] =
    useUpdateClientServiceProfileMutation();
  const [deleteProfile, { loading: deleteProfileLoading }] =
    useDeleteClientServiceProfileMutation();
  const apollo = useApolloClient();

  const clientId = useAuthState((state) => state.data.userInfo.id);
  const { type, id } = useParams<{ type: ServiceProfileType; id?: string }>();
  const { push, pull } = useRoute();
  const { isMobile } = useWindowDimensions();

  const profile = ClientInstance.readFragment({
    id: `ClientServiceProfile:${id}`,
    fragment: ClientServiceProfileFragmentFragmentDoc,
  });

  const [isDefault, setIsDefault] = useState<boolean>(
    id && profile ? profile.primary : true,
  );

  const isInvalidProfile = id && !profile;
  const isInvalidType =
    !type || !Object.keys(ServiceProfileTypeMap).includes(type);

  const handleDeleteProfile = async (id: string) => {
    try {
      const result = await deleteProfile({
        variables: { input: { id } },
      });

      if (result.data?.deleteClientServiceProfile) {
        showToast({
          title: "Success delete service profile",
          type: "success",
        });
      }

      push({
        pageKey: "serviceProfileType",
        params: {
          type: validType,
        },
      });
    } catch (error) {
      const errMsg = getErrorMessage(error, "Failed to delete service profile");
      showToast({
        title: errMsg,
        type: "error",
      });
    }
  };

  const onSave = async (
    data: PetProfileFormData | ElderCareFormData | BabySitterFormData,
  ) => {
    if (validType === "pet") {
      const validationError = validatePetProfile(
        data as PetProfileFormData,
        profiles ?? [],
        !!id,
        profile ?? undefined,
      );

      if (validationError) {
        showToast({
          title: validationError,
          type: "error",
        });
        return;
      }
    }

    const { name, ...profileAttributes } = data;

    const transformedProfileAttributes = transformServiceProfile(
      validType,
      profileAttributes,
    );

    try {
      if (id) {
        const result = await updateProfile({
          variables: {
            input: {
              id,
              name,
              profileAttributes: transformedProfileAttributes,
              primary: isDefault,
            },
          },
        });

        if (result.data?.updateClientServiceProfile) {
          showToast({
            title: "Success update service profile",
            type: "success",
          });
        }
      } else {
        const result = await createProfile({
          type: validType,
          name,
          profileAttributes: transformedProfileAttributes,
          primary: isDefault,
        });

        if (result.data?.createClientServiceProfile) {
          showToast({
            title: "Success create service profile",
            type: "success",
          });
        }
      }

      if (getPlatform() === "web") {
        push({
          pageKey: "serviceProfileType",
          params: {
            type: validType,
          },
        });
      } else {
        pull();
      }
    } catch (error) {
      const errMsg = getErrorMessage(error, "Failed to save service profile");
      showToast({
        title: errMsg,
        type: "error",
      });
      collectError(error, {
        extra: {
          name,
          profileAttributes,
          primary: isDefault,
        },
      });
    }
  };

  const profiles = apollo.readQuery({
    query: ClientServiceProfileByFiltersDocument,
    variables: {
      filters: {
        clientId,
        status: ClientServiceProfileStatusesEnum.Active,
        serviceId: ServiceProfileServiceIdMap[type as ServiceProfileType],
      },
    },
  })?.clientServiceProfileByFilters.clientServiceProfiles;

  const pageTitle = useMemo(() => {
    if (id) {
      return `Edit ${ServiceProfileTypeMap[type as ServiceProfileType]} Profile`;
    }
    return `Create ${ServiceProfileTypeMap[type as ServiceProfileType]} Profile`;
  }, [type, id]);

  const FormContainer = FORM_MAP[type as ServiceProfileType];

  const formRef = useRef<{ submit: () => void }>(null);

  if (isInvalidProfile || isInvalidType || !type) {
    return <Redirect path="/profile/service-profile" />;
  }

  const validType = type;

  const primaryAction = (
    <View className="flex flex-1 flex-row items-center gap-1">
      <Checkbox
        checked={isDefault}
        onCheckedChange={setIsDefault}
        disabled={profiles?.length === 0}
      />
      <Typography variant="body-sm" color="muted-foreground">
        Make Default
      </Typography>
    </View>
  );

  return (
    <ServiceProfileFormScreen
      pageTitle={pageTitle}
      actions={
        <View className="flex flex-1 flex-col gap-4 md:mt-4">
          <Divider className="hidden md:block" />
          <View className="flex flex-row items-center justify-between gap-2">
            {!isMobile && primaryAction}
            <View className="flex flex-1 flex-row gap-2 md:flex-initial">
              {id && !profile?.primary && (
                <Button
                  variant="secondary"
                  color="CTA2"
                  onClick={() => handleDeleteProfile(id)}
                  loading={deleteProfileLoading}
                  className="flex-1 md:flex-initial"
                  fullWidth="full"
                >
                  Delete Profile
                </Button>
              )}
              <Button
                variant="primary"
                color="CTA"
                onClick={() => formRef.current?.submit()}
                className="flex-1 web:min-w-[150px] md:flex-initial"
                loading={createProfileLoading || updateProfileLoading}
                fullWidth="full"
              >
                Save
              </Button>
            </View>
          </View>
        </View>
      }
    >
      <FormContainer id={id} ref={formRef} key={validType + id} onSave={onSave}>
        {isMobile && <View className="mb-[100px] mt-4">{primaryAction}</View>}
      </FormContainer>
    </ServiceProfileFormScreen>
  );
};
